import {isString} from '@gl/lit/utils/index.js';
import type {IChannel, IChannelSubscription} from '@gl/utils/channel.js';
import {Channel, ChannelMessage, ChannelMessageTag} from '@gl/utils/channel.js';

/**
 * Optional parameters for modifying how a preference is managed.
 */
export type PreferenceOptions = {
  /**
   * Whether to prefer the locally stored value over the default provided.
   */
  readOnInit: boolean;
};

/** All preference channel tags. */
export enum PreferenceChannelTags {
  CHANGE = 'change',
}

/**
 * Singleton used for managing preferences.
 */
export class PreferenceManager {
  private static _instance?: PreferenceManager;

  /** Registry of created preferences */
  preferences: Map<string, Preference> = new Map();

  private queuedSubscribers: Map<string, IChannelSubscription[]> = new Map();

  private constructor() {}

  /**
   * Returns the instance of the PreferenceManager singleton.
   */
  static instance(): PreferenceManager {
    if (!PreferenceManager._instance) {
      PreferenceManager._instance = new PreferenceManager();
    }

    return PreferenceManager._instance;
  }

  /**
   * Merges a new subscription with others in a queue.
   */
  private addSubscriberToQueue(
    key: string,
    subscription: IChannelSubscription
  ) {
    const subscriptions = this.queuedSubscribers.get(key) || [];
    this.queuedSubscribers.set(key, [...subscriptions, subscription]);
  }

  /**
   * Attempts to retrieve a preference given a key.
   */
  get<T = unknown>(key: string): Preference<T> | undefined {
    return this.preferences.get(key) as Preference<T> | undefined;
  }

  /**
   * Registers a preference with the PreferenceManager.
   */
  register(preference: Preference) {
    this.preferences.set(preference.key, preference);

    if (this.queuedSubscribers.has(preference.key)) {
      for (const subscription of this.queuedSubscribers.get(preference.key)!) {
        this.subscribe(preference.key, subscription);
      }

      this.queuedSubscribers.delete(preference.key);
    }
  }

  /**
   * Helper to subscribe to a registered preference's channel.
   */
  subscribe(key: string, subscription: IChannelSubscription) {
    if (!this.preferences.has(key)) {
      this.addSubscriberToQueue(key, subscription);
      return;
    }

    this.preferences.get(key)!.channel.subscribe([subscription]);
  }
}

/**
 * Class for mananging a locally stored user preference.
 */
export class Preference<T = unknown> {
  /** Channel object used for watching preference changes. */
  channel: IChannel;
  /** Property indicating when the preference was last modified. */
  lastUpdated = new Date();
  /**
   * The value of the preference upon initialization; used as a default value.
   */
  private initialValue: T;
  /** Instance of the preference manager. */
  private manager: PreferenceManager = PreferenceManager.instance();

  constructor(
    public key: string,
    private _value: T,
    opts?: PreferenceOptions
  ) {
    this.key = key;
    this.channel = new Channel(key);

    this.manager.register(this);

    if (opts?.readOnInit) {
      this.initialValue = _value;
      this.read();
      return;
    }

    this._value = this.initialValue = _value;
  }

  get value() {
    return this._value;
  }

  set value(v: T) {
    this._value = v;
    this.lastUpdated = new Date();
    this.store();

    const tag = ChannelMessageTag.create(PreferenceChannelTags.CHANGE, {
      value: this.value,
    });
    this.channel.publish(ChannelMessage.fromTags([tag]));
  }

  /**
   * Reads the preference from localStorage, given the key.
   */
  read() {
    if (!(this.key in localStorage)) return;

    try {
      const preference = localStorage.getItem(this.key);

      if (!isString(preference)) throw Error('Invalid stored preference data');

      const {lastUpdated, value} = this.deserialize(preference);
      this.lastUpdated = lastUpdated;
      this._value = Object.assign({}, this.initialValue, value);
    } catch (err) {
      console.error(err);
      this.reset();
    }
  }

  /**
   * Safely mutates a preference's value.
   */
  update(mutation: (value: T) => T) {
    this.value = mutation(this.value);
  }

  /**
   * Stores preference in localStorage.
   */
  store() {
    localStorage.setItem(this.key, this.serialize());
  }

  /**
   * Resets the current value to the initial value of the preference.
   */
  reset() {
    this.value = this.initialValue;
  }

  /**
   * Serializes the preference into JSON.
   */
  serialize(): string {
    return JSON.stringify({
      lastUpdated: this.lastUpdated,
      value: this.value,
    });
  }

  /**
   * Deserializes preference JSON into object literal containing 'lastUpdated'
   * and the preference's 'value.'
   */
  deserialize(
    serialized: string
  ): Pick<Preference<T>, 'lastUpdated' | 'value'> {
    return JSON.parse(serialized);
  }
}
